import { CodeMirrorView } from "@repo/fika-blog/src/lib/codeView"
import { CodeBlockNode } from "@repo/fika-blog/src/nodes/code-block"
import type { NodeViewRendererProps } from "@repo/fika-blog/src/tiptap/core"
import type { Node } from "@repo/fika-blog/src/tiptap/pm/model"
import type { EditorView } from "@repo/fika-blog/src/tiptap/pm/view"

class CodeBlockView {
  node: Node
  view: EditorView
  getPos: NodeViewRendererProps["getPos"]
  codeView: CodeMirrorView
  dom: HTMLElement

  constructor(
    node: Node,
    view: EditorView,
    getPos: NodeViewRendererProps["getPos"],
  ) {
    this.node = node
    this.view = view
    this.getPos = getPos

    this.dom = document.createElement("pre")
    const $code = document.createElement("code")
    this.dom.appendChild($code)
    this.dom.setAttribute("data-language", this.node.attrs.language || "text")
    if (this.node.attrs.filename) {
      this.dom.setAttribute("data-filename", this.node.attrs.filename)
    }

    this.codeView = new CodeMirrorView(
      this.node,
      this.view,
      this.getPos,
      $code,
      () => {
        this.handleCodeChange()
      },
      this.node.attrs.language,
    )

    if (this.node.textContent.trim().length === 0) {
      setTimeout(() => {
        this.codeView.cm.focus()
      }, 50)
    }
  }

  handleCodeChange() {
    const currentCode = this.codeView.cm.state.doc.toString()
    const pos = this.getPos()
    if (typeof pos !== "number") return

    this.view.dispatch(
      this.view.state.tr.replaceWith(
        pos + 1,
        pos + this.node.nodeSize - 1,
        this.view.state.schema.text(currentCode),
      ),
    )
  }

  update(node: Node) {
    if (node.type !== this.node.type) return false

    if (node.attrs.language !== this.node.attrs.language) {
      this.dom.setAttribute("data-language", node.attrs.language || "text")
    }

    if (node.attrs.filename !== this.node.attrs.filename) {
      if (node.attrs.filename) {
        this.dom.setAttribute("data-filename", node.attrs.filename)
      } else {
        this.dom.removeAttribute("data-filename")
      }
    }

    this.node = node
    return this.codeView.update(node)
  }

  selectNode() {
    this.codeView.selectNode()
  }

  stopEvent() {
    return this.codeView.stopEvent()
  }

  destroy() {
    this.codeView.destroy()
  }
}

export function getEditCodeBlock() {
  return CodeBlockNode.extend({
    addNodeView() {
      return (props: NodeViewRendererProps) =>
        new CodeBlockView(props.node, props.editor.view, props.getPos)
    },
  }).configure({
    HTMLAttributes: {
      spellcheck: false,
    },
  })
}
